/**
 * /matches/compare?ids=<csv> — side-by-side comparison grid (PR-3 Task 22).
 *
 * Renders matches as columns and comparable fields as rows so admins can
 * eyeball multiple matches at once. Up to 10 ids — backend rejects above
 * (matches index.tsx MULTI_SELECT_CAP).
 *
 * Permission gate (route): `matches.compare`.
 *
 * Backend contract:
 *   GET /v1/admin/matches/compare?ids=<csv>
 *   → { matches: MatchDetail[], summaries_by_match: { [match_id]: SummaryCell[] } }
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.5 / §6.11
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiUrl } from '@refinedev/core';
import { ChevronLeft } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';

interface UserRef {
  id: string;
  email: string | null;
  display_name: string | null;
  is_fake: boolean;
  is_tester: boolean;
}

interface MatchDetailShape {
  match_id: string;
  matched_at: string;
  status: string;
  user1: UserRef;
  user2: UserRef;
  compatibility_score: number | null;
  origin_kind: string;
  admin_flagged_at: string | null;
  admin_flag_reason: string | null;
  created_by_admin_id: string | null;
  has_tester_bypass_artifact: boolean;
}

interface SummaryCell {
  for_user_id: string;
  locale: 'en' | 'es';
  summary: {
    overall_compatibility: number;
    compatibility_tier: string;
    is_stale: boolean;
  } | null;
}

interface ComparePayload {
  matches: MatchDetailShape[];
  summaries_by_match: Record<string, SummaryCell[]>;
}

export default function MatchComparePage() {
  const apiUrl = useApiUrl();
  const [params] = useSearchParams();
  const ids = params.get('ids') || '';
  const [data, setData] = useState<ComparePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const idList = useMemo(
    () => ids.split(',').map(s => s.trim()).filter(Boolean),
    [ids],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    if (idList.length < 2) {
      setErr('Pass at least 2 match IDs as ?ids=id1,id2');
      setLoading(false);
      return;
    }
    if (idList.length > 10) {
      setErr('Maximum 10 matches per compare.');
      setLoading(false);
      return;
    }
    adminFetch(`${apiUrl}/v1/admin/matches/compare?ids=${idList.join(',')}`)
      .then(r => r.json())
      .then((d: ComparePayload) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(
          e instanceof AdminApiError ? e.message : 'Failed to load compare data',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, idList]);

  return (
    <div style={{ padding: 24 }}>
      <nav style={{ marginBottom: 12, fontSize: 13 }}>
        <Link
          to="/matches"
          className="inline-flex items-center gap-1 text-primary no-underline hover:underline"
        >
          <ChevronLeft size={14} /> Back to matches
        </Link>
      </nav>
      <h1 style={{ margin: '0 0 12px 0' }}>
        Compare {data ? data.matches.length : idList.length} matches
      </h1>

      {err && (
        <div className="mb-3 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {err}
        </div>
      )}

      {loading && !data && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      {data && data.matches.length > 0 && (
        <CompareTable
          matches={data.matches}
          summariesByMatch={data.summaries_by_match ?? {}}
        />
      )}
    </div>
  );
}

function CompareTable({
  matches,
  summariesByMatch,
}: {
  matches: MatchDetailShape[];
  summariesByMatch: Record<string, SummaryCell[]>;
}) {
  return (
    <div className="overflow-x-auto rounded-md border bg-card text-card-foreground">
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr className="border-b bg-muted/50 text-muted-foreground">
            <th style={{ ...rowHeader, width: 180 }}>Field</th>
            {matches.map(m => (
              <th key={m.match_id} style={colHeader}>
                <Link
                  to={`/matches/${m.match_id}`}
                  className="text-primary no-underline hover:underline"
                >
                  <code style={{ fontSize: 11 }}>{m.match_id.slice(0, 8)}…</code>
                </Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <Row label="User A">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>
                <UserLabel user={m.user1} />
              </td>
            ))}
          </Row>
          <Row label="User B">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>
                <UserLabel user={m.user2} />
              </td>
            ))}
          </Row>
          <Row label="Matched at">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>
                {formatDate(m.matched_at)}
              </td>
            ))}
          </Row>
          <Row label="Status">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>{m.status}</td>
            ))}
          </Row>
          <Row label="Origin">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>{originLabel(m.origin_kind)}</td>
            ))}
          </Row>
          <Row label="Score">
            {matches.map(m => (
              <td
                key={m.match_id}
                className={`font-bold ${scoreColorClass(m.compatibility_score)}`}
                style={cell}
              >
                {m.compatibility_score ?? '—'}
              </td>
            ))}
          </Row>
          <Row label="Flagged">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>
                {m.admin_flagged_at ? `⚑ ${m.admin_flag_reason ?? ''}` : '—'}
              </td>
            ))}
          </Row>
          <Row label="Admin-created">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>
                {m.created_by_admin_id ? '🔧 yes' : '—'}
              </td>
            ))}
          </Row>
          <Row label="Tester-bypass">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>
                {m.has_tester_bypass_artifact ? '⚠ yes' : '—'}
              </td>
            ))}
          </Row>
          <Row label="Summary state">
            {matches.map(m => (
              <td key={m.match_id} style={cell}>
                <SummaryStateCell
                  match={m}
                  cells={summariesByMatch[m.match_id] ?? []}
                />
              </td>
            ))}
          </Row>
        </tbody>
      </table>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <tr className="border-b">
      <th
        className="bg-muted/50"
        style={{
          ...rowHeader,
          fontWeight: 600,
          textAlign: 'left',
        }}
      >
        {label}
      </th>
      {children}
    </tr>
  );
}

function UserLabel({ user }: { user: UserRef }) {
  const label = user.display_name || user.email || user.id.slice(0, 8);
  return (
    <div>
      <div>{label}</div>
      <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
        {user.is_fake && (
          <span className={`${miniBadgeClass} bg-violet-600`}>fake</span>
        )}
        {user.is_tester && !user.is_fake && (
          <span className={`${miniBadgeClass} bg-sky-500`}>tester</span>
        )}
      </div>
    </div>
  );
}

function SummaryStateCell({
  match,
  cells,
}: {
  match: MatchDetailShape;
  cells: SummaryCell[];
}) {
  if (cells.length === 0) {
    return <span className="text-muted-foreground">no access / none</span>;
  }
  const u1Lines = cells
    .filter(c => c.for_user_id === match.user1.id)
    .map(c => formatCell(c));
  const u2Lines = cells
    .filter(c => c.for_user_id === match.user2.id)
    .map(c => formatCell(c));
  return (
    <div style={{ fontSize: 11, lineHeight: 1.4 }}>
      <div>A: {u1Lines.length ? u1Lines.join(', ') : '—'}</div>
      <div>B: {u2Lines.length ? u2Lines.join(', ') : '—'}</div>
    </div>
  );
}

function formatCell(c: SummaryCell): string {
  if (!c.summary) return `${c.locale}=missing`;
  const staleSuffix = c.summary.is_stale ? '⚠' : '';
  return `${c.locale}=${c.summary.overall_compatibility}${staleSuffix}`;
}

function originLabel(kind: string) {
  switch (kind) {
    case 'organic':
      return 'Organic';
    case 'tester_instant':
      return 'Tester instant';
    case 'fake_paired':
      return 'Fake-paired';
    default:
      return kind;
  }
}

function scoreColorClass(s: number | null): string {
  if (s === null || s === undefined) return 'text-muted-foreground';
  if (s >= 85) return 'text-green-500';
  if (s >= 65) return 'text-amber-500';
  return 'text-red-500';
}

function formatDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

// Vertical column separators use the theme border token so they adapt to
// light/dark; horizontal row dividers come from the `border-b` class on <Row>.
const COL_DIVIDER = '1px solid hsl(var(--border))';
const rowHeader: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  borderRight: COL_DIVIDER,
};
const colHeader: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  textAlign: 'left',
  borderRight: COL_DIVIDER,
};
const cell: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  verticalAlign: 'top',
  borderRight: COL_DIVIDER,
};
const miniBadgeClass =
  'inline-block rounded-full px-1.5 py-px text-[10px] uppercase tracking-wide text-white';
